import { useState, useEffect } from "react";

// Detect iOS devices
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Check if running in standalone mode (installed)
const isStandalone = () => {
  return window.matchMedia("(display-mode: standalone)").matches ||
         (window.navigator.standalone === true) ||
         document.referrer.includes('android-app://');
};

export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    const ios = isIOS();
    setIsIOSDevice(ios);
    
    // Check if app is already installed
    const standalone = isStandalone();
    setIsInstalled(standalone);

    // For iOS, show install option if not in standalone mode
    if (ios && !standalone) {
      setIsInstallable(true);
      return;
    }

    // For Android/Chrome, listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", () => {});
    };
  }, []);

  const promptInstall = async () => {
    // For iOS, we can't programmatically trigger install
    // The user needs to manually add to home screen
    if (isIOSDevice) {
      return false; // Return false so the prompt can show instructions
    }

    if (!deferredPrompt) {
      return false;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setIsInstallable(false);
      return true;
    }

    return false;
  };

  return {
    isInstallable: isInstallable && !isInstalled,
    isInstalled,
    isIOSDevice,
    promptInstall,
  };
};
